import * as Sentry from "@sentry/browser";
import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router";
import { router } from "./router";
import "./index.css";
import { SWRConfig } from "swr";
import { ModalProvider } from "~/components/Modal";
import { SnackBarProvider } from "~/components/SnackBar";
import { fetcher } from "~/fetcher";

// Sentry
const dsn = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.VITE_ENV_TYPE;

// filename が解決できない（cross-origin で inject された等の）スタックフレームか。
const isAnonymousFrame = (frame: { filename?: string }) =>
	!frame.filename ||
	frame.filename === "<anonymous>" ||
	frame.filename === "undefined";

if (import.meta.env.VITE_SENTRY_DSN) {
	Sentry.init({
		dsn,
		environment,
		// 例外の発生元フレーム（throw位置）が triton 自身のオリジン以外
		// （Adobe Typekit、ブラウザ拡張/内蔵機能が注入する翻訳スクリプト等）の場合は送信しない。
		// フォントローダー等のサードパーティスクリプト起因のエラーは UX に影響しないノイズ
		// であるため、個別の URL を都度列挙せず一括で抑制する。
		// Cookiebot (同意バナー) は失敗するとバナー自体が表示されず UX に直結するため、
		// ノイズとして抑制せず監視対象として allowUrls に含める。
		// ref: Sentry T4-ACCOUNT-FRONTEND-5R, T4-ACCOUNT-FRONTEND-A7
		allowUrls: [window.location.origin, "https://consent.cookiebot.com"],
		ignoreErrors: [
			// ネットワーク起因のAxiosエラー（ユーザー側の問題で開発側でアクション不要）
			/AxiosError: Network Error/,
			/AxiosError: Request aborted/,
			// クライアント側のリクエストタイムアウト（中国→Tokyo 等の経路・回線起因。サーバー遅延は Datadog で監視）
			// ref: Sentry T4-ACCOUNT-FRONTEND-A2
			/AxiosError: timeout exceeded/,
			// サードパーティスクリプト（フォントローダー等）のネットワークエラー
			/Network request failed/,
			// WeChat (微信) 内蔵ブラウザが注入するスクリプト由来のノイズ
			// ref: Sentry T4-ACCOUNT-FRONTEND-A0
			/WeixinJSBridge/,
			// Adobe Typekit ローダーの fetch 失敗（minify でフレーム URL を解決できない場合の保険）
			// ref: Sentry T4-ACCOUNT-FRONTEND-5R
			/Failed to fetch: "https:\/\/use\.typekit\.net/,
		],
		// Chrome Mobile iOS 等が cross-origin で inject するスクリプト由来の anonymized エラーを drop する
		// （全フレームの filename が解決できない場合のみ落とすので、triton bundle 由来のエラーは送信される）
		// ref: Sentry T4-ACCOUNT-FRONTEND-6M
		beforeSend(event) {
			const values = event.exception?.values;
			if (!values || values.length === 0) {
				return event;
			}
			const allAnonymized = values.every((value) => {
				const frames = value.stacktrace?.frames;
				if (!frames) {
					return true;
				}
				if (frames.length === 0) {
					return false;
				}
				return frames.every(isAnonymousFrame);
			});
			if (allAnonymized) {
				return null;
			}

			// VivoBrowser 等が注入するスクリプトが responseType='arraybuffer' の XHR から
			// responseText を読もうとして発生する仕様違反エラー。Triton 側に該当コードは無く、
			// Sentry SDK の addEventListener ラッパ（browserApiErrors）が拾うノイズ。
			// first-party (triton bundle) フレームを含まない場合のみ drop し、自前コード起因
			// （first-party フレームあり）なら送信を維持して見逃しを防ぐ。
			// ref: Sentry T4-ACCOUNT-FRONTEND-A1
			const isResponseTextNoise = values.some((value) =>
				/Failed to read the 'responseText' property from 'XMLHttpRequest'/.test(
					value.value ?? "",
				),
			);
			if (isResponseTextNoise) {
				const hasFirstPartyFrame = values.some((value) =>
					(value.stacktrace?.frames ?? []).some(
						(frame) =>
							!isAnonymousFrame(frame) &&
							!frame.filename?.includes("node_modules"),
					),
				);
				if (!hasFirstPartyFrame) {
					return null;
				}
			}

			return event;
		},
	});
}

// #root は index.html に常に存在する。null になるのは HTML/DOM を正常に
// ロードできないクライアント環境（bot・特殊ブラウザ等）のみで、その場合に
// createRoot(null) が "Target container is not a DOM element." (React error
// #299) を throw し Sentry にノイズとして上がる。描画先が無い環境なので no-op する。
// ref: Sentry T4-ACCOUNT-FRONTEND-A6
const container = document.getElementById("root");
if (container) {
	ReactDOM.createRoot(container).render(
		<React.StrictMode>
			<SWRConfig
				value={{
					fetcher,
				}}
			>
				<ModalProvider>
					<SnackBarProvider>
						<RouterProvider router={router} />
					</SnackBarProvider>
				</ModalProvider>
			</SWRConfig>
		</React.StrictMode>,
	);
}
